import { dispatch, useSelect, select } from '@wordpress/data';
import { useCallback } from '@wordpress/element';
import { extensionCartUpdate } from '@woocommerce/blocks-checkout';

import { YWCDD_STORE_KEY as storeKey } from '../../../data/';
import {
	getAvailableDates,
	getCarrierOptions,
	getMaxDate,
	getMinDate,
	getTimeSlotOptions,
} from '../../utils';

export const useDeliveryDate = () => {
	const {
		selectedCarrier,
		selectedDate,
		selectedProcessingMethod,
		selectedSlot,
		selectedRate,
		carrierOptions,
		availableDates,
		availableSlots,
		minDate,
		maxDate,
		isRequired,
		isInitialized,
	} = useSelect( ( select ) => {
		const isEditor = !! select( 'core/editor' );
		const store = select( storeKey );
		const wcStore = select( 'wc/store/cart' );
		const rates = wcStore.getShippingRates();
		const currentRates = Object.fromEntries(
			rates.map(
				( { package_id: packageId, shipping_rates: packageRates } ) => [
					packageId,
					packageRates.find( ( rate ) => rate.selected )?.rate_id ||
						'',
				]
			)
		);

		const newRateId =
			Object.keys( currentRates ).length === 0
				? ''
				: Object.values( currentRates ).shift();
		const oldRateID = store.getCurrentRateId();
		const isShippingRateBeingSelected =
			wcStore.isShippingRateBeingSelected();
		if ( newRateId !== oldRateID && isShippingRateBeingSelected ) {
			dispatch( storeKey ).invalidateResolutionForStoreSelector(
				'initialize'
			);
		}
		let initalized = false;
		if( isEditor ) {
			initalized = store.initialize( newRateId, isEditor );
		}else{
			initalized =
				'' !== newRateId ? store.initialize( newRateId ) : false;
		}
		const required = initalized ? store.isRequired() : false;
		const formattedCarrierOptions = initalized
			? getCarrierOptions( store.getCarriers(), required )
			: [];
		const formattedAvailableDates = initalized
			? getAvailableDates( store.getDates() )
			: [];
		const formattedAvailableSlots = initalized
			? getTimeSlotOptions( store.getTimeSlots(), required )
			: [];
		const min = initalized ? getMinDate( store.getDates() ) : null;
		const max = initalized ? getMaxDate( store.getDates() ) : null;

		let date = store.getSelectedDate();

		date = date !== '' ? new Date( date * 1000 ) : null;
		return {
			selectedCarrier: store.getSelectedCarrierID(),
			selectedDate: date,
			selectedProcessingMethod: store.getProcessingMethodID(),
			selectedSlot: store.getSelectedSlot(),
			selectedRate: newRateId,
			carrierOptions: formattedCarrierOptions,
			availableDates: formattedAvailableDates,
			isRequired: required,
			availableSlots: formattedAvailableSlots,
			minDate: min,
			maxDate: max,
			isInitialized: initalized,
		};
	} );

	const updateCalendar = useCallback(
		( carrierId ) => {
			if (
				isInitialized &&
				typeof selectedProcessingMethod !== 'undefined' &&
				typeof carrierId !== 'undefined' &&
				selectedCarrier !== carrierId
			) {
				const store = select( storeKey );
				dispatch( storeKey ).invalidateResolutionForStoreSelector(
					'updateDates'
				);
				store.updateDates( carrierId, selectedProcessingMethod );
			}
		},
		[ isInitialized, selectedProcessingMethod, selectedCarrier ]
	);

	const updateSlots = useCallback(
		( newDate ) => {
			if ( newDate !== selectedDate ) {
				const store = select( storeKey );
				dispatch( storeKey ).invalidateResolutionForStoreSelector(
					'updateTimeSlots'
				);
				store.updateTimeSlots( store.getSelectedCarrierID(), newDate );
			}
		},
		[ isInitialized, selectedDate ]
	);

	const maybeAddFee = useCallback(
		( slotId ) => {
			if ( selectedSlot !== slotId ) {
				const store = select( storeKey );
				const slots = getTimeSlotOptions( store.getTimeSlots() );
				const slotArgs = slots
					.filter( ( slot ) => slot.value === slotId )
					.shift();

				dispatch( storeKey ).invalidateResolutionForStoreSelector(
					'addCartFee'
				);
				store.addCartFee( slotId );
				extensionCartUpdate( {
					namespace: 'ywcdd/delivery-date',
					data: {
						action: 'maybe_add_fee',
						slot_id: slotArgs.value,
						value: slotArgs.feeValue,
						name: slotArgs.feeName,
					},
				} );
			}
		},
		[ selectedSlot ]
	);

	const changeStatus = ( eventType, value ) => {
		if ( 'carrier' === eventType ) {
			updateCalendar( value );
		} else if ( 'date' === eventType ) {
			const date = new Date( value );
			const year = date.toLocaleString( 'default', { year: 'numeric' } );
			const month = date.toLocaleString( 'default', {
				month: '2-digit',
			} );
			const day = date.toLocaleString( 'default', { day: '2-digit' } );
			updateSlots( year + '-' + month + '-' + day );
		} else if ( 'slot' === eventType ) {
			maybeAddFee( value );
		}
	};
	const removeFee = () => {
		extensionCartUpdate( {
			namespace: 'ywcdd/delivery-date',
			data: {
				action: 'maybe_add_fee',
			},
		} );
	};
	return {
		carrierOptions,
		availableDates,
		availableSlots,
		minDate,
		maxDate,
		selectedCarrier,
		selectedDate,
		selectedProcessingMethod,
		selectedSlot,
		selectedRate,
		isRequired,
		isInitialized,
		removeFee,
		changeStatus,
	};
};
